import api, { type User, type Invitation } from './api';

/**
 * Pagination metadata returned by the backend.
 */
export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * Fetch all users (admin only).
 * Backend returns { data: User[], pagination: Pagination }.
 */
export async function getUsers(
  page = 1,
  limit = 50
): Promise<{ data: User[]; pagination: Pagination }> {
  const response = await api.get('/users', { params: { page, limit } });
  return response.data;
}

/**
 * Fetch the current authenticated user's profile.
 */
export async function getCurrentUser(): Promise<User> {
  const response = await api.get<User>('/users/me');
  return response.data;
}

/**
 * Update a user's profile (admin only for other users, self for own profile).
 * Supports: displayName, role, isActive, accessExpiresAt.
 */
export async function updateUser(
  id: number,
  data: Partial<Pick<User, 'displayName' | 'role' | 'isActive'>> & {
    accessExpiresAt?: string | null;
  }
): Promise<User> {
  const response = await api.patch<User>(`/users/${id}`, data);
  return response.data;
}

/**
 * Permanently delete a user and cascade-clean their references (admin only).
 * Backend blocks self-delete and last-active-admin delete.
 */
export async function deleteUser(id: number): Promise<void> {
  await api.delete(`/users/${id}`);
}

/**
 * Invite an external user by email with a specified access duration.
 */
export async function inviteUser(
  email: string,
  durationDays: number,
  displayName?: string,
  permittedTabs?: string[]
): Promise<Invitation> {
  const response = await api.post('/users/invite', {
    email,
    durationDays,
    displayName,
    permittedTabs,
  });
  return response.data;
}

/**
 * Fetch all invitations (admin only).
 * Backend returns { data: Invitation[] }.
 */
export async function getInvitations(): Promise<Invitation[]> {
  const response = await api.get('/users/invitations');
  return response.data.data ?? response.data;
}

/**
 * Revoke an active invitation.
 */
export async function revokeInvitation(id: number): Promise<void> {
  await api.delete(`/users/invitations/${id}`);
}

/**
 * Resend an expired/revoked invitation with a new token.
 */
export async function resendInvitation(id: number): Promise<{ message: string; emailSent: boolean }> {
  const response = await api.post(`/users/invitations/${id}/resend`);
  return response.data;
}

/**
 * Extend the validity of an invitation.
 */
export async function extendInvitation(id: number, days: number): Promise<{ message: string }> {
  const response = await api.put(`/users/invitations/${id}/extend`, { days });
  return response.data;
}

/**
 * Permanently delete an invitation.
 */
export async function deleteInvitation(id: number): Promise<void> {
  await api.delete(`/users/invitations/${id}/delete`);
}
